import { clone } from 'ramda';
import { noop } from 'utils/noop';
import { SceneType, ScalingType } from 'types/Scene';
import { Effect } from 'types/Effects';
import { BASE_SIZE } from 'constants/canvas';
import Konva from 'konva';
import effectToKonvaTextSettings from 'utils/konva/effectToKonvaTextSettings';
import textSceneFunc from 'utils/konva/textSceneFunc';

const cnvdiv = document.createElement('div');

const stage = new Konva.Stage({
  container: cnvdiv,
  width: 0,
  height: 0
});

const layer = new Konva.Layer();

stage.add(layer);

export type ScenePreviewSettings = {
  src: string;
  start: number;
  trimStart: number;
  duration: number;
  isPlaying: boolean;
  type: SceneType;
  id: string;
  scalingMode: ScalingType;
  effects?: Effect[];
};

export type RenderFn = (ctx: CanvasRenderingContext2D, preview: ScenePreview) => void;

export type EffectPreview = {
  beforeFrameDrawn: RenderFn;
  afterFrameDrawn: RenderFn;
  afterSettingsUpdated?: (preview: ScenePreview) => void;
};

export default abstract class ScenePreview<T = any> {
  protected defaultSettings: ScenePreviewSettings;

  settings: ScenePreviewSettings;

  time: number = 0;

  protected element: T;

  protected isVisible = false;

  protected beforeFrameDrawn: RenderFn = noop;

  protected afterFrameDrawn: RenderFn = noop;

  abstract drawFrame(ctx: CanvasRenderingContext2D): void;

  protected afterSettingsUpdated: EffectPreview['afterSettingsUpdated'] = noop;

  abstract getOriginalWidth(): number;

  abstract getOriginalHeight(): number;

  abstract updateVolume(volume: number): void;

  abstract getSize(ctx: CanvasRenderingContext2D): [number, number, number];

  pause() {}

  enableSound() {}

  attachEffect(setup: EffectPreview) {
    this.beforeFrameDrawn = setup.beforeFrameDrawn;
    this.afterFrameDrawn = setup.afterFrameDrawn;
    this.afterSettingsUpdated = setup.afterSettingsUpdated ?? noop;

    this.updateSettings(this.defaultSettings);
  }

  abstract updateSpecificSettings(): void;

  updateSettings(settings: Partial<ScenePreviewSettings>) {
    this.defaultSettings = { ...this.defaultSettings, ...settings };
    this.settings = clone(this.defaultSettings);
    this.updateSpecificSettings();
    this.afterSettingsUpdated(this);
  }

  setTime(time: number) {
    const start = this.settings.start;
    this.time = time - start;
    this.isVisible = time >= start && time <= start + this.settings.duration - 1;
  }

  getScaleFactor(ctx: CanvasRenderingContext2D) {
    return ctx.canvas.height / BASE_SIZE;
  }

  getOffsetLeft(ctx: CanvasRenderingContext2D) {
    const [factWidth] = this.getSize(ctx);
    return (ctx.canvas.width - factWidth) / 2;
  }

  getOffsetTop(ctx: CanvasRenderingContext2D) {
    const [, factHeight] = this.getSize(ctx);
    return (ctx.canvas.height - factHeight) / 2;
  }

  drawEffects(ctx: CanvasRenderingContext2D) {
    this.settings.effects?.forEach(effect => {
      if (effect.name === 'text') {
        this.drawText(ctx, effect);
      }
    });
  }

  drawText(ctx: CanvasRenderingContext2D, effect: Effect) {
    const scaleFactor = this.getScaleFactor(ctx);

    layer.children.each(e => e.destroy());

    const konvaGroup = new Konva.Group({
      id: effect.id,
      size: { width: ctx.canvas.width, height: ctx.canvas.height },
      scale: { x: scaleFactor, y: scaleFactor },
      position: { x: 0, y: 0 }
    });

    stage.setSize({ width: ctx.canvas.width, height: ctx.canvas.height });

    const textNode = new Konva.Text(effectToKonvaTextSettings({ ...effect }));
    textNode._sceneFunc = textSceneFunc;

    konvaGroup.add(textNode);
    layer.add(konvaGroup);
    layer.draw();

    ctx.drawImage(layer.getCanvas()._canvas, 0, 0, ctx.canvas.width, ctx.canvas.height);
  }

  getElement() {
    return this.element;
  }

  render(ctx: CanvasRenderingContext2D): void {
    if (!this.isVisible) return;

    this.beforeFrameDrawn(ctx, this);

    ctx.fillStyle = '#000';

    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    this.drawFrame(ctx);
    this.settings.isPlaying && this.drawEffects(ctx);
    this.afterFrameDrawn(ctx, this);
  }
}
